"use client";

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import {
  ArrowsRightLeftIcon,
  FolderArrowDownIcon,
  MusicalNoteIcon,
  ScissorsIcon,
  UserGroupIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import { HeartIcon } from "@heroicons/react/24/solid";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";

import { MenuItem } from "@/app/(application)/components/sidebar/MenuItem";
import { TimeLeftCard } from "@/app/(application)/components/sidebar/TimeLeftCard";
import { UpgradeButton } from "@/app/(application)/components/sidebar/UpgradeButton";
import { TextLink } from "@/components/links/TextLink";
import { NormalText } from "@/components/text/NormalText";
import { affiliateLink } from "@/constants/navigation";
import { getUserCountryCode } from "@/data/auth/queries/getUserCountryCode";
import { ArrowSplitIcon } from "@/icons/ArrowSplitIcon";
import { PianoRollIcon } from "@/icons/PianoRollIcon";
import { UserNoDates } from "@/types/types";

export function Menu({
  user,
  hideTimeLeft,
  clickHandler,
  showCovers,
  hasTtvAccess,
  className,
}: {
  user: UserNoDates | null;
  hideTimeLeft: boolean;
  clickHandler: (() => void) | null;
  showCovers: boolean;
  hasTtvAccess: boolean;
  className?: string;
}) {
  const [showSampleDespiteCountry, setShowSampleDespiteCountry] =
    useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const userCountryCode = await getUserCountryCode();

      if (userCountryCode !== null && userCountryCode === "IN") {
        setShowSampleDespiteCountry(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div
      className={twMerge("flex flex-col justify-between gap-y-6", className)}
    >
      <div>
        {/* Text to vocals is the only Studio tool, so the whole section stays hidden until the rollout admits the user. */}
        {hasTtvAccess && (
          <>
            <NormalText elementType="span" className="text-xs text-gray-400">
              STUDIO
            </NormalText>
            <ul className="-mx-2 mb-2 space-y-1">
              <MenuItem
                navItem={{
                  name: "Text to vocals",
                  href: "/text-to-vocals",
                  icon: PianoRollIcon,
                }}
                isNew={false}
                isBeta={true}
                clickHandler={clickHandler}
              />
            </ul>
          </>
        )}

        <NormalText elementType="span" className="text-xs text-gray-400">
          VOCALS
        </NormalText>
        <ul className="-mx-2 mb-2 space-y-1">
          <MenuItem
            navItem={{
              name: "Convert vocals",
              href: "/create",
              icon: MusicalNoteIcon,
            }}
            isNew={false}
            isBeta={false}
            clickHandler={clickHandler}
          />
          <MenuItem
            navItem={{
              name: "Isolate vocals",
              href: "/isolate",
              icon: ScissorsIcon,
            }}
            isNew={false}
            isBeta={false}
            clickHandler={clickHandler}
          />
          <MenuItem
            navItem={{
              name: "Make harmonies",
              href: "/harmonies",
              icon: UserGroupIcon,
            }}
            isNew={false}
            isBeta={false}
            clickHandler={clickHandler}
          />
          <MenuItem
            navItem={{
              name: "Stem splitter",
              href: "/stem-splitter",
              icon: ArrowSplitIcon,
            }}
            isNew={false}
            isBeta={false}
            clickHandler={clickHandler}
          />
          {showSampleDespiteCountry && showCovers && (
            <MenuItem
              navItem={{
                name: "Cover acapellas",
                href: "/covers",
                icon: FolderArrowDownIcon,
              }}
              isNew={false}
              isBeta={false}
              clickHandler={clickHandler}
            />
          )}
          <MenuItem
            navItem={{
              name: "Library",
              href: "/library",
              icon: HeartIcon,
            }}
            isNew={false}
            isBeta={false}
            clickHandler={clickHandler}
          />
        </ul>

        <NormalText elementType="span" className="text-xs text-gray-400">
          VOICES
        </NormalText>
        <ul className="-mx-2 space-y-1">
          <MenuItem
            navItem={{
              name: "My voices",
              href: "/my-voices",
              icon: UsersIcon,
            }}
            isNew={false}
            isBeta={false}
            clickHandler={clickHandler}
          />
          <MenuItem
            navItem={{
              name: "Mixed voices",
              href: "/mixed-voices",
              icon: ArrowsRightLeftIcon,
            }}
            isNew={false}
            isBeta={false}
            clickHandler={clickHandler}
          />
        </ul>

        <hr className="my-4" />

        <div className="flex flex-col gap-y-3">
          <TextLink
            href="/pricing"
            external={false}
            className="text-gray-500 hover:text-gray-900"
          >
            Plans & Pricing
          </TextLink>

          <TextLink
            href={affiliateLink}
            external={true}
            className="text-gray-500 hover:text-gray-900"
          >
            Affiliate
            <ArrowTopRightOnSquareIcon className="mb-1 ml-2 inline h-4 w-4" />
          </TextLink>

          <TextLink
            href="/contact"
            external={false}
            className="text-gray-500 hover:text-gray-900"
          >
            Contact
          </TextLink>
        </div>
      </div>

      {user !== null && (
        <div className="mt-auto">
          <UpgradeButton
            user={user}
            className="mb-4"
            closeMobileSidebar={clickHandler}
          />
          {!hideTimeLeft && <TimeLeftCard user={user} />}
        </div>
      )}
    </div>
  );
}
